import React, { useEffect, useState } from 'react';
import { Contract, ethers } from 'ethers';
import { useEthers } from '@usedapp/core';
import { toast } from 'react-toastify';
import CoinflipContractABI from '../../../abi/coinflip.json';
import axios from 'axios';
import rules from '../../common/coinflip/coinflip_rules.json';
import Popup from '.././coinflipPopup';
import CoinflipCard from '../../common/coinflip/CoinflipCard';
import coinflipConfig from "./config.json";
import { useRef } from 'react';
import InfoModal from "./coinflipInfoModal";

const NEW_CONTRACT_ADDRESS = "0x3e14eE8B7ab2aAaca537881C8A7C467B8E713D36"; // Your contract address
const ADMIN_WALLET = "0xBFf06DEb7059B475a734E71D101b2D9fF3247cFF".toLowerCase(); // Admin wallet lowercase

const CoinflipGame = () => {
    const { account, library, activateBrowserWallet } = useEthers();

    const [signer, setSigner] = useState(null);
    const [coinflipContract, setCoinflipContract] = useState(null);

    const [selectedSide, setSelectedSide] = useState(null);
    const [betCRO, setBetCRO] = useState('');
    const [maxBet, setMaxBet] = useState(ethers.utils.parseEther("100")); // max 100 CRO default

    const [isBetting, setIsBetting] = useState(false);
    const [isFlipping, setIsFlipping] = useState(false);

    const [rewardBalance, setRewardBalance] = useState(0);
    const [croBalance, setCroBalance] = useState(0);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupOutcome, setPopupOutcome] = useState(null);
    const [flipOutcome, setFlipOutcome] = useState(null);

    const [coinflips, setCoinflips] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const flipsPerPage = 2;

    const [activeTab, setActiveTab] = useState("game");
    const [jackpotActive, setJackpotActive] = useState(false);

    const [finalSide, setFinalSide] = useState(null); // 'Heads' or 'Tails'
    const [showCoin, setShowCoin] = useState(true);
    const [fundAmount, setFundAmount] = useState('');
    const [contractBalance, setContractBalance] = useState(ethers.BigNumber.from("0"));
    const [walletReady, setWalletReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showWalletTimeoutMessage, setShowWalletTimeoutMessage] = useState(false);




    // Jackpot stats states
    const [jackpotEntries, setJackpotEntries] = useState(0);
    const [jackpotPool, setJackpotPool] = useState(ethers.BigNumber.from("0"));
    const [providerContract, setProviderContract] = useState(null);

    const selectedSideRef = useRef();
    const betCRORef = useRef();

    useEffect(() => {
        selectedSideRef.current = selectedSide;
        betCRORef.current = betCRO;
    }, [selectedSide, betCRO]);

    useEffect(() => {
        if (library && account) {
            const signerInstance = library.getSigner(account);
            setSigner(signerInstance);
            const signerContract = new Contract(NEW_CONTRACT_ADDRESS, CoinflipContractABI, signerInstance);
            setCoinflipContract(signerContract);

            // create a provider contract for listening to events
            // library.provider is the low-level provider (JsonRpcProvider / Web3Provider.provider)
            const provider = library.provider ? new ethers.providers.Web3Provider(library.provider) : library;
            const providerContract = new Contract(NEW_CONTRACT_ADDRESS, CoinflipContractABI, provider);
            // store provider contract on `window` or local ref if you want access elsewhere
            // but we will set it into state (so other effects can use it)
            setProviderContract(providerContract);

            setWalletReady(true);
            setIsLoading(false);
        } else {
            setSigner(null);
            setCoinflipContract(null);
            setProviderContract(null);
            setWalletReady(false);
            setIsLoading(false); // allow app to show even without wallet
        }
    }, [library, account]);

    useEffect(() => {
        if (isLoading) {
            const timeout = setTimeout(() => {
                setShowWalletTimeoutMessage(true);
            }, 8000); // 8 seconds

            return () => clearTimeout(timeout); // Cleanup
        } else {
            setShowWalletTimeoutMessage(false); // Wallet ready
        }
    }, [isLoading]);

    useEffect(() => {
        if (!coinflipContract) return;
        const fetchMaxBet = async () => {
            try {
                const max = await coinflipContract.getMaxBet();
                setMaxBet(max);
            } catch (err) {
                console.error("Failed to fetch maxBet:", err);
            }
        };
        fetchMaxBet();
    }, [coinflipContract]);

    // Fetch Jackpot Stats
    useEffect(() => {
        const fetchJackpotStats = async () => {
            if (!coinflipContract) return;
            try {
                const pool = await coinflipContract.getJackpotPool();
                let entries = 0;
                if (coinflipContract.getJackpotEntries) {
                    entries = (await coinflipContract.getJackpotEntries()).toNumber();
                }
                setJackpotPool(pool);
                setJackpotEntries(entries);
            } catch (err) {
                console.error("Error fetching jackpot stats:", err);
            }
        };
        fetchJackpotStats();
    }, [coinflipContract]);

    useEffect(() => {
        if (providerContract) console.log('providerContract available for events');
    }, [providerContract]);

    // Add a function to manually refresh jackpot entries on button click
    const refreshJackpotEntries = async () => {
        if (!coinflipContract) return;
        try {
            if (coinflipContract.getJackpotEntries) {
                const entries = (await coinflipContract.getJackpotEntries()).toNumber();
                setJackpotEntries(entries);
                toast.success("Jackpot entries updated");
            } else {
                toast.error("Contract does not support getJackpotEntries");
            }
        } catch (err) {
            toast.error("Failed to fetch jackpot entries");
            console.error(err);
        }
    };

    // const handleFlip = async () => {
    //     setIsFlipping(true);
    //     setFinalSide(null); // reset
    //     setShowPopup(false);
    //
    //     // Simulate flipping duration
    //     setTimeout(() => {
    //         const isWin = popupOutcome === 'win';
    //         const resultSide = isWin ? selectedSide : (selectedSide === 'Heads' ? 'Tails' : 'Heads');
    //
    //         setFinalSide(resultSide);
    //         setIsFlipping(false);
    //         setShowPopup(true);
    //     }, 2000); // match CSS animation duration
    // };

    useEffect(() => {
        if (!coinflipContract) return;

        const fetchJackpotData = async () => {
            try {
                const active = await coinflipContract.getJackpotStatus();
                setJackpotActive(active);
                console.log("gadfsgdfsg "+active);

                const pool = await coinflipContract.getJackpotPool();
                setJackpotPool(pool);

            } catch (err) {
                console.error("Error fetching jackpot data:", err);
            }
        };

        fetchJackpotData();
    }, [coinflipContract]);

    const fundContract = async () => {
        try {
            if (!account) return activateBrowserWallet();
            if (account.toLowerCase() !== ADMIN_WALLET) {
                toast.error("Not authorized to fund the contract.");
                return;
            }

            const value = parseFloat(fundAmount);
            if (isNaN(value) || value <= 0) {
                toast.error("Enter a valid amount");
                return;
            }

            const tx = await coinflipContract.fundContract({
                value: ethers.utils.parseEther(fundAmount)
            });
            await tx.wait();
            toast.success("Contract funded successfully!");
            setFundAmount('');
            fetchContractBalance();
        } catch (error) {
            console.error("Fund error:", error);
            toast.error(error?.message || "Failed to fund contract.");
        }
    };

    const fetchContractBalance = async () => {
        try {
            const balance = await library.getBalance(NEW_CONTRACT_ADDRESS);
            setContractBalance(balance);
        } catch (error) {
            console.error("Failed to fetch contract balance:", error);
        }
    };

    useEffect(() => {
        if (account?.toLowerCase() === ADMIN_WALLET && library) {
            fetchContractBalance();
        }
    }, [account, library, coinflipContract]);

    // Keep betCRO numeric and capped at maxBet
    const handleBetInput = (value) => {
        let num = parseFloat(value);
        if (isNaN(num) || num <= 0) {
            setBetCRO('');
            return;
        }
        const maxCRO = parseFloat(ethers.utils.formatEther(maxBet));
        if (num > maxCRO) num = maxCRO;
        setBetCRO(num.toString());
    };

    const fetchRewardBalance = async () => {
        try {
            if (!coinflipContract || !account) return;
            const balance = await coinflipContract.getPlayerWinnings(account);
            setRewardBalance(balance.toString());
        } catch (error) {
            console.error('Error fetching reward balance:', error);
        }
    };

    useEffect(() => {
        if (!rewardBalance) return setCroBalance(0);
        setCroBalance(parseFloat(ethers.utils.formatEther(rewardBalance)));
    }, [rewardBalance]);

    useEffect(() => {
        fetchRewardBalance();
    }, [coinflipContract, account]);

    useEffect(() => {
        if (!providerContract || !account) return;

        const filter = providerContract.filters.flipFinished(); // adjust if ABI differs

        const onFlipFinished = (...args) => {
            const event = args[args.length - 1];
            try {
                const eventArgs = event && event.args ? event.args : args;
                const player = eventArgs.player ? eventArgs.player : eventArgs[0];

                if (player.toLowerCase() !== account.toLowerCase()) return; // ignore flips from other users

                const outcomeInt = eventArgs.result ? eventArgs.result.toNumber() : (eventArgs[1] ? eventArgs[1].toNumber() : eventArgs[0].toNumber());
                const resultStr = outcomeInt === 0 ? "Heads" : "Tails";
                const isWin = outcomeInt === (selectedSideRef.current === "Heads" ? 0 : 1);

                setFinalSide(resultStr);
                setPopupOutcome(isWin ? "Win" : "Lose");
                setFlipOutcome(isWin ? "Win" : "Lose");
                setShowPopup(true);
                setShowCoin(true);

                addCoinFlip(account, resultStr, selectedSideRef.current, betCRORef.current);
                fetchRewardBalance();

                setIsBetting(false);
                setIsFlipping(false);

                setTimeout(() => {
                    setShowPopup(false);
                    setShowCoin(false);
                    setFinalSide(null);
                }, 5000);

            } catch (err) {
                console.error("Error in flipFinished listener:", err, args);
            }
        };

        // Remove any existing listeners first to prevent duplicates
        providerContract.removeAllListeners(filter);
        providerContract.on(filter, onFlipFinished);
        console.log("Listening to flipFinished events");

        return () => {
            try {
                providerContract.off(filter, onFlipFinished);
                console.log("Removed flipFinished listener");
            } catch (err) {
                console.warn("Error removing listener:", err);
            }
        };
    }, [providerContract, account]);


    async function addCoinFlip(player, result, selected, betCRO) {
        try {
            let betAmount = betCRO;
            if (!player || !result || !selected) throw new Error('Missing fields');
            await axios.post(`${coinflipConfig.apiURI}/coinflips`, {
                player, result, selected, betAmount, chain: 'Cronos'
            });
        } catch (error) {
            console.error('Error adding coinflip:', error);
        }
    }

    const placeBet = async () => {
        try {
            if (!account) return activateBrowserWallet();
            if (!selectedSide) throw new Error("Please select Heads or Tails.");
            if (!betCRO || parseFloat(betCRO) <= 0) throw new Error("Enter a valid bet amount.");

            const betValue = ethers.utils.parseEther(betCRO.toString());
            if (betValue.gt(maxBet)) {
                toast.error(`Bet exceeds max allowed of ${ethers.utils.formatEther(maxBet)} CRO`);
                return;
            }

            if (!coinflipContract) throw new Error("Contract not ready");

            setIsBetting(true);
            setIsFlipping(true);
            setShowCoin(true);
            setFinalSide(null);

            const sideInt = selectedSide === "Heads" ? 0 : 1;

            // ✅ Explicitly increase gasLimit
            const tx = await coinflipContract.flip(sideInt, {
                value: betValue,
                gasLimit: ethers.BigNumber.from("500000") // 500k gas — adjust as needed
            });
            toast.info("Transaction sent, waiting for confirmation...");

            // Fallback timeout for mobile wallets
            const timeoutMs = 15000;
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Transaction pending — still waiting for confirmation")), timeoutMs)
            );

            let receipt;
            try {
                receipt = await Promise.race([tx.wait(1), timeoutPromise]);
            } catch (err) {
                console.warn("tx.wait fallback:", err);
                receipt = await library.getTransactionReceipt(tx.hash);
            }

            // Parse flipFinished from receipt logs
            let flipHandled = false;
            if (receipt && receipt.logs.length) {
                for (const log of receipt.logs) {
                    try {
                        const parsed = coinflipContract.interface.parseLog(log);
                        if (parsed.name === "flipFinished") {
                            const outcomeInt = parsed.args.result.toNumber();
                            const resultStr = outcomeInt === 0 ? "Heads" : "Tails";
                            const isWin = outcomeInt === (selectedSideRef.current === "Heads" ? 0 : 1);

                            setFinalSide(resultStr);
                            setPopupOutcome(isWin ? "Win" : "Lose");
                            setFlipOutcome(isWin ? "Win" : "Lose");
                            setShowPopup(true);
                            setShowCoin(true);

                            await addCoinFlip(account, resultStr, selectedSideRef.current, betCRORef.current);
                            await fetchRewardBalance();

                            flipHandled = true;
                            break;
                        }
                    } catch {}
                }
            }

            if (!flipHandled) {
                toast.info("Flip is pending — result will appear once the network confirms.");
            }

        } catch (error) {
            console.error("placeBet error:", error);
            toast.error(error?.message || "Bet failed.");
        } finally {
            setIsBetting(false);
            setTimeout(() => setIsFlipping(false), 500);
        }
    };


    const claimRewards = async () => {
        try {
            if (!account) return activateBrowserWallet();
            const tx = await coinflipContract.withdrawUserWinnings();
            await tx.wait();
            toast.success('Rewards claimed!');
            fetchRewardBalance();
        } catch (error) {
            toast.error(error?.message);
        }
    };

    const pickJackpotWinner = async () => {
        try {
            if (!account) return activateBrowserWallet();
            if (account.toLowerCase() !== ADMIN_WALLET) {
                toast.error("You are not authorized to pick jackpot winner.");
                return;
            }
            const tx = await coinflipContract.pickJackpotWinner();
            await tx.wait();
            toast.success("Jackpot winner picked!");
            // Refresh jackpot stats after winner picked
            if (coinflipContract.getJackpotEntries) {
                const entries = (await coinflipContract.getJackpotEntries()).toNumber();
                setJackpotEntries(entries);
            } else {
                setJackpotEntries(0);
            }
            const pool = await coinflipContract.getJackpotPool();
            setJackpotPool(pool);
        } catch (error) {
            toast.error(error?.message || "Failed to pick jackpot winner.");
        }
    };

    const toggleJackpot = async () => {
        try {
            if (!account) return activateBrowserWallet();
            if (account.toLowerCase() !== ADMIN_WALLET) {
                toast.error("Not authorized.");
                return;
            }

            // 🔁 Always fetch the latest status from the contract
            const currentStatus = await coinflipContract.getJackpotStatus();
            const newStatus = !currentStatus;

            const tx = await coinflipContract.toggleJackpot(newStatus);
            await tx.wait();

            toast.success(`Jackpot ${newStatus ? "enabled" : "disabled"}!`);
            setJackpotActive(newStatus); // ✅ Update local state
        } catch (error) {
            console.error("Failed to toggle jackpot:", error);
            toast.error(error?.message || "Toggle failed.");
        }
    };


    const indexOfLastFlip = currentPage * flipsPerPage;
    const indexOfFirstFlip = indexOfLastFlip - flipsPerPage;
    const currentFlips = coinflips.slice(indexOfFirstFlip, indexOfLastFlip);

    const handleClickNext = () => {
        if (currentPage < Math.ceil(coinflips.length / flipsPerPage)) setCurrentPage(prev => prev + 1);
    };

    const handleClickPrev = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    useEffect(() => {
        const fetchFlips = async () => {
            try {
                const response = await axios.get(`${coinflipConfig.apiURI}/coinflips/Cronos`);
                setCoinflips(response.data);
            } catch (error) {
                console.error('Error fetching flips:', error);
            }
        };
        fetchFlips();
    }, []);

    const calculateWinRate = () => {
        if (!account) return { wins: 0, losses: 0, total: 0, winRate: 0 };
        const address = account.toLowerCase();
        const myFlips = coinflips.filter(flip => flip.player.toLowerCase() === address);
        const wins = myFlips.filter(f => f.result === f.selected).length;
        const losses = myFlips.length - wins;
        const winRate = myFlips.length ? ((wins / myFlips.length) * 100).toFixed(2) : 0;
        return { wins, losses, total: myFlips.length, winRate };
    };

    const formatNumber = (num) => {
        if (Number.isInteger(num)) return num.toString();
        return num.toString().replace(/\.?0+$/, '');
    };

    const buttonStyle = {
        borderRadius: '20px', backgroundColor: '#fff', color: '#0047FF',
        padding: '10px 20px', margin: '5px', cursor: 'pointer', border: 'none'
    };

    const headsButtonStyle = {
        ...buttonStyle,
        backgroundColor: selectedSide === 'Heads' ? '#0047FF' : '#fff',
        color: selectedSide === 'Heads' ? '#fff' : '#0047FF',
    };

    const tailsButtonStyle = {
        ...buttonStyle,
        backgroundColor: selectedSide === 'Tails' ? '#0047FF' : '#fff',
        color: selectedSide === 'Tails' ? '#fff' : '#0047FF',
    };

    const linkButtonStyle = {
        background: 'none', border: 'none', color: '#00FFD1',
        textDecoration: 'underline', cursor: 'pointer', padding: '0', fontWeight: 'bold'
    };

    const tabButtonStyle = (tab) => ({
        padding: '10px 25px',
        marginRight: '10px',
        cursor: 'pointer',
        borderRadius: '10px',
        border: 'none',
        fontWeight: activeTab === tab ? 'bold' : 'normal',
        backgroundColor: activeTab === tab ? '#0047FF' : '#1c1f2e',
        color: activeTab === tab ? '#fff' : '#888',
        transition: 'background-color 0.3s, color 0.3s',
    });

    const jackpotBannerStyle = {
        padding: '10px 20px',
        borderRadius: '25px',
        fontWeight: 'bold',
        fontSize: '1.8rem',
        marginBottom: '20px',
        width: 'fit-content',
        marginLeft: 'auto',
        marginRight: 'auto',
        boxShadow: jackpotActive
            ? '0 0 15px 3px limegreen'
            : '0 0 12px 2px #3399ff',
        color: jackpotActive ? 'limegreen' : '#3399ff',
        backgroundColor: jackpotActive ? 'rgba(50,205,50,0.15)' : 'rgba(51,153,255,0.15)',
        textShadow: jackpotActive ? '0 0 10px limegreen' : '0 0 8px #3399ff',
        userSelect: 'none',
    };

    // Jackpot Active banner color depends on jackpotPool > 0
    const jackpotActiveColor = jackpotPool.gt(0) ? 'limegreen' : 'red';

    if (isLoading) {
        return (
            <div style={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#0A0D1C',
                color: '#00FFD1',
                textAlign: 'center',
                padding: '20px'
            }}>
                <div style={{fontSize: '2rem', marginBottom: '1rem'}}>🪙 Initializing Coinflip...</div>
                <div style={{fontSize: '1rem', color: '#888'}}>Please wait while we connect to your wallet and the
                    Cronos network.
                </div>
                {showWalletTimeoutMessage && (
                    <div style={{color: 'red', marginTop: '2rem'}}>
                        ❗ It’s taking too long. Please reconnect your wallet manually.
                    </div>
                )}
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        marginTop: '20px',
                        padding: '10px 20px',
                        fontSize: '1rem',
                        borderRadius: '10px',
                        backgroundColor: '#00FFD1',
                        color: '#12162e',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                >
                    Retry Connection
                </button>
            </div>
        );
    }

    return (

    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#0A0D1C',
        padding: '20px'
    }}>
        <div className="card project-card" style={{
            padding: '20px',
            textAlign: 'center',
            width: '100%',
            maxWidth: '700px',
            backgroundColor: '#12162e', borderRadius: '15px', boxShadow: '0 0 20px #0047FF' }}>

                {/* Tabs */}
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                    <button style={tabButtonStyle("game")} onClick={() => setActiveTab("game")}>Play Game</button>
                    <button style={tabButtonStyle("flips")} onClick={() => setActiveTab("flips")}>View Flips</button>
                    <button style={tabButtonStyle("stats")} onClick={() => setActiveTab("stats")}>My Stats</button>
                    <button
                        style={tabButtonStyle("jackpot")}
                        onClick={() => setActiveTab("jackpot")}
                    >
                        Jackpot Stats
                    </button>
                    {account && account.toLowerCase() === ADMIN_WALLET && (
                        <button
                            style={tabButtonStyle("admin")}
                            onClick={() => setActiveTab("admin")}
                        >
                            Old Man Button
                        </button>
                    )}
                </div>

                {/* === GAME TAB === */}
                {activeTab === "game" && (
                    <>
                        <div style={jackpotBannerStyle}>
                            {jackpotActive ? '🟢 Jackpot Active' : '🔴 Jackpot Inactive'}
                        </div>
                        <h2 style={{color: '#fff'}}>Timmy Flip (CRONOS)</h2>
                        <div className="betting-options" style={{marginBottom: '15px'}}>
                            <button style={headsButtonStyle} onClick={() => setSelectedSide('Heads')}
                                    disabled={isBetting}>Heads
                            </button>
                            <button style={tailsButtonStyle} onClick={() => setSelectedSide('Tails')}
                                    disabled={isBetting}>Tails
                            </button>
                        </div>

                        <div style={{marginBottom: '10px'}}>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Enter CRO amount"
                                value={betCRO}
                                onChange={(e) => handleBetInput(e.target.value)}
                                disabled={isBetting}
                                style={{
                                    padding: "10px",
                                    fontSize: "16px",
                                    width: "140px",
                                    textAlign: "center",
                                    borderRadius: "8px",
                                    border: "1px solid #0047FF",
                                    marginRight: '10px',
                                    color: '#0047FF',
                                    backgroundColor: '#1c1f2e',
                                    outline: 'none'
                                }}
                            />
                            <button
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#00FFD1',
                                    color: '#12162e',
                                    padding: '10px 15px'
                                }}
                                onClick={() => {
                                    const maxCRO = parseFloat(ethers.utils.formatEther(maxBet));
                                    setBetCRO(maxCRO.toString());
                                }}
                                disabled={isBetting}
                            >
                                Max Bet
                            </button>
                        </div>
                        <div style={{marginBottom: '20px', height: '120px'}}>
                            {isFlipping && showCoin && (
                                <div className="coin flip-animation">🪙</div>
                            )}

                            {!isFlipping && finalSide && showCoin && (
                                <div
                                    className="coin result-coin"
                                    style={{
                                        fontSize: '2rem',
                                        color: flipOutcome === 'win' ? 'limegreen' : 'red',
                                        transition: 'opacity 0.5s ease, color 0.5s ease',
                                        opacity: showCoin ? 1 : 0,
                                    }}
                                >
                                    {finalSide === 'Heads' ? "🙂 Heads" : "👑 Tails"}
                                </div>
                            )}
                        </div>

                        <div style={{marginBottom: '20px', color: '#00FFD1'}}>
                            Bet Amount: <strong>{formatNumber(parseFloat(betCRO) || 0)}</strong> CRO (Max 100 CRO)
                        </div>

                        <button style={{...buttonStyle, width: '100%', backgroundColor: '#0047FF', color: '#fff'}}
                                onClick={placeBet} disabled={!selectedSide || isBetting || !betCRO}>
                            {isBetting ? 'Placing Bet...' : 'Place Bet'}
                        </button>

                        <div>
                            <button
                                style={{
                                    ...buttonStyle,
                                    marginTop: '20px',
                                    width: '100%',
                                    backgroundColor: '#00FFD1',
                                    color: '#12162e'
                                }}
                                onClick={claimRewards}
                                disabled={croBalance === 0 || isBetting}
                            >
                                Claim {formatNumber(croBalance)} CRO
                            </button>
                        </div>
                        <br/>
                        <button style={linkButtonStyle} onClick={() => setIsModalOpen(true)}>
                            View rules and information.
                        </button>
                    </>
                )}

                {/* === FLIPS TAB === */}
            {activeTab === "flips" && (
                <>
                    <h3 style={{ color: '#00FFD1', marginBottom: '20px' }}>🎲 Timmy Flips</h3>

                    {currentFlips.length === 0 ? (
                        <p style={{ color: '#888' }}>No flips yet. Be the first to flip the coin!</p>
                    ) : (
                        currentFlips.map(flip => (
                            <CoinflipCard key={flip._id} flip={flip} />
                        ))
                    )}

                    <div className="pagination" style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button style={buttonStyle} onClick={handleClickPrev} disabled={currentPage === 1}>
                            ◀ Previous
                        </button>
                        <button
                            style={buttonStyle}
                            onClick={handleClickNext}
                            disabled={currentPage === Math.ceil(coinflips.length / flipsPerPage)}
                        >
                            Next ▶
                        </button>
                    </div>
                </>
            )}

                {/* === MY STATS TAB === */}
                {activeTab === "stats" && (
                    <>
                        <h3 style={{ color: '#00FFD1' }}>🎯 My Flip Stats</h3>
                        {account ? (() => {
                            const { wins, losses, total, winRate } = calculateWinRate();
                            return (
                                <div style={{
                                    backgroundColor: '#0A0D1C',
                                    padding: '20px',
                                    borderRadius: '10px',
                                    boxShadow: '0 2px 10px rgba(0, 71, 255, 0.3)',
                                    color: '#ffffff',
                                    textAlign: 'left',
                                    width: '100%',
                                    maxWidth: '600px'
                                }}>
                                    <p><strong style={{ color: '#00FFD1' }}>Wallet:</strong> {account}</p>
                                    <p><strong style={{ color: '#00FFD1' }}>Total Flips:</strong> {total}</p>
                                    <p><strong style={{ color: '#00FFD1' }}>Wins:</strong> {wins}</p>
                                    <p><strong style={{ color: '#00FFD1' }}>Losses:</strong> {losses}</p>
                                    <p><strong style={{ color: '#00FFD1' }}>Win Rate:</strong> {winRate}%</p>

                                    <div className="progress-bar-container" style={{ marginTop: '20px', height: '20px', width: '100%', backgroundColor: '#1c1f2e', borderRadius: '10px', overflow: 'hidden' }}>
                                        <div
                                            className="progress-bar"
                                            style={{
                                                height: '100%',
                                                width: `${winRate}%`,
                                                backgroundColor: '#00FFD1',
                                                transition: 'width 0.5s ease-in-out'
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })() : <p>Please connect your wallet to see your stats.</p>}
                    </>
                )}

                {/* === JACKPOT STATS TAB === */}
                {activeTab === "jackpot" && (
                    <>
                        <h3 style={{ color: '#00FFD1' }}>Jackpot Stats</h3>
                        <p>Total Jackpot Pool: <strong>{ethers.utils.formatEther(jackpotPool)} CRO</strong></p>
                        <p>Total Jackpot Entries: <strong>{jackpotEntries}</strong></p>
                        <button
                            style={{ ...buttonStyle, backgroundColor: '#00FFD1', color: '#12162e', marginTop: '10px', width: '100%' }}
                            onClick={refreshJackpotEntries}
                        >
                            Get Jackpot Entries
                        </button>
                    </>
                )}

                {/* === ADMIN TAB === */}
                {activeTab === "admin" && account && account.toLowerCase() === ADMIN_WALLET && (
                    <>
                        <h3 style={{color: '#00FFD1'}}>Old Man Button</h3>
                        <p>Pick a jackpot winner to distribute the jackpot pool.</p>
                        <button
                            style={{...buttonStyle, backgroundColor: '#0047FF', color: '#fff', width: '100%'}}
                            onClick={pickJackpotWinner}
                        >
                            Pick Jackpot Winner
                        </button>
                        <button
                            style={{
                                ...buttonStyle,
                                backgroundColor: jackpotActive ? 'red' : 'limegreen',
                                color: '#12162e',
                                width: '100%',
                            }}
                            onClick={toggleJackpot}
                        >
                            {jackpotActive ? "Disable Jackpot" : "Enable Jackpot"}
                        </button>

                        {/* Fund Contract Section */}
                        <div style={{marginTop: '30px'}}>
                            <h4 style={{color: '#00FFD1'}}>Fund Contract</h4>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Amount in CRO"
                                    value={fundAmount}
                                    onChange={(e) => setFundAmount(e.target.value)}
                                    style={{
                                        padding: '10px',
                                        fontSize: '16px',
                                        width: '160px',
                                        borderRadius: '8px',
                                        border: '1px solid #0047FF',
                                        color: '#0047FF',
                                        backgroundColor: '#1c1f2e',
                                        outline: 'none'
                                    }}
                                />
                                <button
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: '#00FFD1',
                                        color: '#12162e'
                                    }}
                                    onClick={fundContract}
                                >
                                    Fund
                                </button>
                            </div>

                            {/* Contract Balance */}
                            <div style={{marginTop: '15px', color: '#00FFD1'}}>
                                Contract Balance:{" "}
                                <strong style={{color: '#fff'}}>
                                    {ethers.utils.formatEther(contractBalance)} CRO
                                </strong>
                            </div>
                        </div>
                    </>
                )}

                {/* Rules Modal */}
                {isModalOpen && (
                    <InfoModal close={() => setIsModalOpen(false)}>
                        <h2 style={{color: '#00FFD1'}}>Rules & Info</h2>
                        <p style={{ color: '#fff' }}>{rules.rules}</p>
                        <button style={{ ...buttonStyle, marginTop: '20px' }} onClick={() => setIsModalOpen(false)}>Close</button>
                    </InfoModal>
                )}

                {/* Flip Result Popup */}
                {/*{showPopup && (*/}
                {/*    <Popup close={() => setShowPopup(false)}>*/}
                {/*        {popupOutcome === 'win' && <h3 style={{ color: 'lime' }}>🎉 You Win!</h3>}*/}
                {/*        {popupOutcome === 'lose' && <h3 style={{ color: 'red' }}>😢 You Lose!</h3>}*/}
                {/*    </Popup>*/}
                {/*)}*/}
            </div>
        </div>
    );
};

export default CoinflipGame;
